import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";
import { Button, Flex, FlexItem } from "@wordpress/components";
import { useSelect, withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment, useMemo } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React from "react";
import { Icon } from "../../components";
import { useBlockStyle } from "../../hooks";
import "../assets/sass/blocks/featured-categories/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "@blocks/utils";
import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
	cat1: Array<any>;
	cat2: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		cat1,
		cat2,
		className,
		attributes: {
			category,
			category2,
			postCount,
			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
			label,
			label2,
			postBoxStyle,
			postTitleMarkup,
			hoverAnimation,
			enableCategory,
			enableComment,
			metaPosition,
			enableAuthor,
			enableDate,
			separatorType,
			enableIcon,
			enableExcerpt,
			excerptLimit,
			enableReadMore,
			readMoreText,
			hideOnDesktop,
			size,
			enableViewMore,
			viewMoreText,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreLink,
			viewButtonPosition,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "featured-categories",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	const currentCategoryIndex2 = useMemo(() => {
		return categories.findIndex((cat) => cat.id === parseInt(category2));
	}, [category2, categories]);

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const currentCategoryIndex = useMemo(() => {
		return categories
			? categories.findIndex((cat) => cat.id === parseInt(category))
			: -1; // Use -1 or another appropriate default value
	}, [category, categories]);

	const classNames = classnames(
		`mzb-featured-categories mzb-featured-categories-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop"
	);

	const classNames2 = classnames(`mzb-post-heading mzb-${headingLayout}`, {
		[`mzb-${headingLayout1AdvancedStyle}`]:
			headingLayout === `heading-layout-1`,
		[`mzb-${headingLayout2AdvancedStyle}`]:
			headingLayout === `heading-layout-2`,
		[`mzb-${headingLayout3AdvancedStyle}`]:
			headingLayout === `heading-layout-3`,
		[`mzb-${headingLayout4AdvancedStyle}`]:
			headingLayout === `heading-layout-4`,
		[`mzb-${headingLayout5AdvancedStyle}`]:
			headingLayout === `heading-layout-5`,
		[`mzb-${headingLayout6AdvancedStyle}`]:
			headingLayout === `heading-layout-6`,
		[`mzb-${headingLayout7AdvancedStyle}`]:
			headingLayout === `heading-layout-7`,
		[`mzb-${headingLayout8AdvancedStyle}`]:
			headingLayout === `heading-layout-8`,
		[`mzb-${headingLayout9AdvancedStyle}`]:
			headingLayout === `heading-layout-9`,
	});

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / postCount)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	const TagName = postTitleMarkup || "h6";

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				<div className={`mzb-category-posts`}>
					{
						<div
							className={`mzb-category-1-posts mzb-${postBoxStyle}`}
						>
							<div className={classNames2}>
								{enableHeading === true && (
									<h2 className="mzb-heading-text">
										{categories?.[currentCategoryIndex]
											?.name
											? categories?.[currentCategoryIndex]
													?.name
											: label}
									</h2>
								)}
								{viewButtonPosition === "top" &&
									enableViewMore && (
										<div className="mzb-view-more">
											{/*eslint-disable-next-line react/jsx-no-target-blank */}
											<a
												href={"#"}
												target={
													viewMoreLink?.newTab
														? "_blank"
														: "_self"
												}
												rel={[
													viewMoreLink?.newTab
														? "noreferrer noopener"
														: "",
													viewMoreLink?.noFollow
														? "nofollow"
														: "",
												]
													.filter(Boolean)
													.join(" ")}
											>
												<p>{viewMoreText}</p>
												{enableViewMoreIcon && (
													<Icon
														type="frontendIcon"
														name={viewMoreIcon}
														size={24}
													/>
												)}
											</a>
										</div>
									)}
							</div>
							<Fragment>
								{(cat1 || []).map((post, idx) => {
									const maxWords = excerptLimit; // Replace with your desired word limit
									const excerpt = post?.excerpt?.rendered
										.split(" ")
										.slice(0, maxWords)
										.join(" ");

									return (
										<div className={`mzb-post`} key={idx}>
											{post
												?.magazine_blocks_featured_image_url
												?.full?.[0] && (
												<div
													className={`mzb-featured-image ${hoverAnimation}`}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full[0]
														}
													/>
												</div>
											)}
											<div className="mzb-post-content">
												{(enableCategory ||
													enableComment) && (
													<div className="mzb-post-meta">
														{enableCategory && (
															<span
																className="mzb-post-categories"
																dangerouslySetInnerHTML={{
																	__html: post?.magazine_blocks_category,
																}}
															/>
														)}
														{enableComment && (
															<span className="comments-link">
																<Icon
																	type="frontendIcon"
																	name="commentIcon"
																	size={24}
																/>{" "}
																{
																	post?.magazine_blocks_comment
																}
															</span>
														)}
													</div>
												)}

												{(enableAuthor || enableDate) &&
													"top" === metaPosition && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableAuthor ===
																true && (
																<span className="mzb-post-author">
																	{enableIcon ===
																		true && (
																		<img
																			className="post-author-image"
																			src={
																				post?.magazine_blocks_author_image
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{
																			post
																				?.magazine_blocks_author
																				?.display_name
																		}{" "}
																	</a>{" "}
																</span>
															)}
															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}

												<>
													<a href={"#"}>
														<TagName className="mzb-post-title">
															{
																post?.title
																	?.rendered
															}
														</TagName>
													</a>
												</>

												{(enableAuthor || enableDate) &&
													"bottom" ===
														metaPosition && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableAuthor ===
																true && (
																<span className="mzb-post-author">
																	{enableIcon ===
																		true && (
																		<img
																			className="post-author-image"
																			src={
																				post?.magazine_blocks_author_image
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{
																			post
																				?.magazine_blocks_author
																				?.display_name
																		}{" "}
																	</a>{" "}
																</span>
															)}
															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}
												{(enableExcerpt ||
													enableReadMore) && (
													<div className="mzb-entry-content">
														{enableExcerpt && (
															<div
																className="mzb-entry-summary"
																dangerouslySetInnerHTML={{
																	__html: excerpt,
																}}
															/>
														)}
														{enableReadMore && (
															<div className="mzb-read-more">
																<a href="#">
																	{
																		readMoreText
																	}
																</a>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</Fragment>
							{viewButtonPosition === "bottom" &&
								enableViewMore && (
									<div className="mzb-view-more">
										{/*eslint-disable-next-line react/jsx-no-target-blank */}
										<a
											href={"#"}
											target={
												viewMoreLink?.newTab
													? "_blank"
													: "_self"
											}
											rel={[
												viewMoreLink?.newTab
													? "noreferrer noopener"
													: "",
												viewMoreLink?.noFollow
													? "nofollow"
													: "",
											]
												.filter(Boolean)
												.join(" ")}
										>
											<p>{viewMoreText}</p>
											{enableViewMoreIcon && (
												<Icon
													type="frontendIcon"
													name={viewMoreIcon}
													size={24}
												/>
											)}
										</a>
									</div>
								)}
						</div>
					}
					{
						<div
							className={`mzb-category-2-posts mzb-${postBoxStyle}`}
						>
							<div className={classNames2}>
								{enableHeading === true && (
									<h2 className="mzb-heading-text">
										{categories?.[currentCategoryIndex2]
											?.name
											? categories?.[
													currentCategoryIndex2
											  ]?.name
											: label2}
									</h2>
								)}
								{viewButtonPosition === "top" &&
									enableViewMore && (
										<div className="mzb-view-more">
											{/*eslint-disable-next-line react/jsx-no-target-blank */}
											<a
												href={"#"}
												target={
													viewMoreLink?.newTab
														? "_blank"
														: "_self"
												}
												rel={[
													viewMoreLink?.newTab
														? "noreferrer noopener"
														: "",
													viewMoreLink?.noFollow
														? "nofollow"
														: "",
												]
													.filter(Boolean)
													.join(" ")}
											>
												<p>{viewMoreText}</p>
												{enableViewMoreIcon && (
													<Icon
														type="frontendIcon"
														name={viewMoreIcon}
														size={24}
													/>
												)}
											</a>
										</div>
									)}
							</div>
							<Fragment>
								{(cat2 || []).map((post, idx) => {
									const maxWords = excerptLimit; // Replace with your desired word limit
									const excerpt = post?.excerpt?.rendered
										.split(" ")
										.slice(0, maxWords)
										.join(" ");

									return (
										<div className={`mzb-post`} key={idx}>
											{post
												?.magazine_blocks_featured_image_url
												?.full?.[0] && (
												<div
													className={`mzb-featured-image ${hoverAnimation}`}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full?.[0]
														}
													/>
												</div>
											)}
											<div className="mzb-post-content">
												{(enableCategory ||
													enableComment) && (
													<div className="mzb-post-meta">
														{enableCategory && (
															<span
																className="mzb-post-categories"
																dangerouslySetInnerHTML={{
																	__html: post?.magazine_blocks_category,
																}}
															/>
														)}
														{enableComment && (
															<span className="comments-link">
																<Icon
																	type="frontendIcon"
																	name="commentIcon"
																	size={24}
																/>{" "}
																{
																	post?.magazine_blocks_comment
																}
															</span>
														)}
													</div>
												)}

												{(enableAuthor || enableDate) &&
													"top" === metaPosition && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}

												<>
													<a href={"#"}>
														<TagName className="mzb-post-title">
															{
																post?.title
																	?.rendered
															}
														</TagName>
													</a>
												</>

												{(enableAuthor || enableDate) &&
													"bottom" ===
														metaPosition && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}
												{(enableExcerpt ||
													enableReadMore) && (
													<div className="mzb-entry-content">
														{enableExcerpt && (
															<div
																className="mzb-entry-summary"
																dangerouslySetInnerHTML={{
																	__html: excerpt,
																}}
															/>
														)}
														{enableReadMore && (
															<div className="mzb-read-more">
																<a href="#">
																	{
																		readMoreText
																	}
																</a>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</Fragment>
							{viewButtonPosition === "bottom" &&
								enableViewMore && (
									<div className="mzb-view-more">
										{/*eslint-disable-next-line react/jsx-no-target-blank */}
										<a
											href={"#"}
											target={
												viewMoreLink?.newTab
													? "_blank"
													: "_self"
											}
											rel={[
												viewMoreLink?.newTab
													? "noreferrer noopener"
													: "",
												viewMoreLink?.noFollow
													? "nofollow"
													: "",
											]
												.filter(Boolean)
												.join(" ")}
										>
											<p>{viewMoreText}</p>

											{enableViewMoreIcon && (
												<Icon
													type="frontendIcon"
													name={viewMoreIcon}
													size={24}
												/>
											)}
										</a>
									</div>
								)}
						</div>
					}
				</div>
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			postCount,
			offset,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			page,
			category2,
			tag2,
			orderBy2,
			orderType2,
			authorName2,
			excludedCategory2,
			postType,
			postType2,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";
	const selectedPostType2 = isTemplate ? "post" : postType2 || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);
	const shouldSkipFilters2 = isTemplate && (isArchive || isSearch);

	const baseQuery: any = {
		per_page: postCount,
		offset: offset || 0,
		page,
	};

	const cat1Query: any = {
		...baseQuery,
		orderby: orderBy,
		order: orderType,
		author: authorName === "all" ? undefined : authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			cat1Query.categories = parseInt(category, 10);
		}
		if (tag && tag !== "all") {
			cat1Query.tags = tag;
		}
		if (excludedCategory !== "") {
			cat1Query.categories_exclude = excludedCategory;
		}
	}

	const cat2Query = {
		...baseQuery,
		orderby: orderBy2,
		order: orderType2,
		author: authorName2 === "all" ? undefined : authorName2,
	};

	if (!shouldSkipFilters2) {
		if (category2 && category2 !== "all") {
			cat2Query.categories = parseInt(category2, 10);
		}
		if (tag2 && tag2 !== "all") {
			cat2Query.tags = tag2;
		}
		if (excludedCategory2 !== "") {
			cat2Query.categories_exclude = excludedCategory2;
		}
	}

	const numberOfPostsQuery1: Record<string, any> = {
		per_page: -1,
	};

	if (!shouldSkipFilters && category && category !== "all") {
		numberOfPostsQuery1.categories = parseInt(category, 10);
	}

	const numberOfPostsQuery2: Record<string, any> = {
		per_page: -1,
	};

	if (!shouldSkipFilters2 && category2 && category2 !== "all") {
		numberOfPostsQuery2.categories = parseInt(category2, 10);
	}

	return {
		cat1: getEntityRecords("postType", selectedPostType, cat1Query) || [],
		cat2: getEntityRecords("postType", selectedPostType2, cat2Query) || [],
		numberOfPosts1:
			getEntityRecords("postType", selectedPostType, numberOfPostsQuery1)
				?.length || 0,
		numberOfPosts2:
			getEntityRecords("postType", selectedPostType2, numberOfPostsQuery2)
				?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
